import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { boxerService } from "../services/boxerService";
import { useAuth } from "./AuthContext";
import { useNotification } from "./NotificationContext";

const ChatSessionContext = createContext(null);

export function ChatSessionProvider({ children }) {
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [sessions, setSessions]               = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages]               = useState([]);
  const [queryMode, setQueryMode]             = useState("schema");
  const [sessionsLoaded, setSessionsLoaded]   = useState(false);
  const [savedPrompts, setSavedPrompts]       = useState([]);
  const [pendingPromptText, setPendingPromptText] = useState(null);

  // Reset all session state when the logged-in user changes
  const prevUsernameRef = useRef(null);
  useEffect(() => {
    const currentUsername = user?.username ?? null;
    if (prevUsernameRef.current !== null && prevUsernameRef.current !== currentUsername) {
      setSessions([]);
      setCurrentSessionId(null);
      setMessages([]);
      setQueryMode("schema");
      setSessionsLoaded(false);
      setSavedPrompts([]);
      setPendingPromptText(null);
    }
    prevUsernameRef.current = currentUsername;
  }, [user?.username]);

  const loadSessions = useCallback(async () => {
    try {
      const s = await boxerService.getSessions();
      setSessions(s);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoaded(true);
    }
  }, []);

  const openSession = useCallback(async (session) => {
    try {
      const data = await boxerService.getSession(session.session_id);
      setQueryMode(data.query_mode || "schema");
      setMessages(data.messages || []);
      setCurrentSessionId(session.session_id);
    } catch {
      addNotification("error", "Failed to open chat session");
    }
  }, [addNotification]);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setCurrentSessionId(null);
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    try {
      await boxerService.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      setCurrentSessionId(prev => prev === sessionId ? null : prev);
      setMessages(prev => currentSessionId === sessionId ? [] : prev);
      addNotification("success", "Chat deleted");
    } catch {}
  }, [currentSessionId, addNotification]);

  const addSession = useCallback((session) => {
    setSessions(prev => [session, ...prev]);
  }, []);

  const renameSession = useCallback(async (sessionId, title) => {
    try {
      await boxerService.renameSession(sessionId, title);
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, title } : s
      ));
      addNotification("success", "Chat renamed");
    } catch {}
  }, [addNotification]);

  const saveMessages = useCallback(async (msgs, sessionId) => {
    if (!sessionId) return;
    try {
      await boxerService.saveMessages(sessionId, msgs);
    } catch {}
  }, []);

  const loadSavedPrompts = useCallback(async () => {
    try {
      const prompts = await boxerService.getSavedPrompts();
      setSavedPrompts(prompts);
    } catch {
      setSavedPrompts([]);
    }
  }, []);

  const createSavedPrompt = useCallback(async (title, content, mode) => {
    try {
      const prompt = await boxerService.createSavedPrompt(title, content, mode);
      setSavedPrompts(prev => [prompt, ...prev]);
      addNotification("success", "Prompt saved");
      return true;
    } catch {
      addNotification("error", "Failed to save prompt");
      return false;
    }
  }, [addNotification]);

  const deleteSavedPrompt = useCallback(async (promptId) => {
    try {
      await boxerService.deleteSavedPrompt(promptId);
      setSavedPrompts(prev => prev.filter(p => p.prompt_id !== promptId));
      addNotification("success", "Prompt deleted");
    } catch {}
  }, [addNotification]);

  const pinSession = useCallback(async (sessionId, pinned) => {
    try {
      await boxerService.pinSession(sessionId, pinned);
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, pinned } : s
      ));
      addNotification("success", pinned ? "Chat pinned" : "Chat unpinned");
    } catch {}
  }, [addNotification]);

  const clearAllSessions = useCallback(async () => {
    await boxerService.clearAllSessions();
    setSessions([]);
    setCurrentSessionId(null);
    setMessages([]);
    addNotification("success", "All chats cleared");
  }, [addNotification]);

  const clearAllPrompts = useCallback(async () => {
    await boxerService.clearAllPrompts();
    setSavedPrompts([]);
    addNotification("success", "All saved prompts cleared");
  }, [addNotification]);

  return (
    <ChatSessionContext.Provider value={{
      sessions, setSessions,
      currentSessionId, setCurrentSessionId,
      messages, setMessages,
      queryMode, setQueryMode,
      sessionsLoaded,
      loadSessions,
      openSession,
      startNewChat,
      deleteSession,
      addSession,
      saveMessages,
      renameSession,
      savedPrompts,
      loadSavedPrompts,
      createSavedPrompt,
      deleteSavedPrompt,
      pinSession,
      clearAllSessions,
      clearAllPrompts,
      pendingPromptText,
      setPendingPromptText,
    }}>
      {children}
    </ChatSessionContext.Provider>
  );
}

export const useChatSession = () => {
  const ctx = useContext(ChatSessionContext);
  if (!ctx) throw new Error("useChatSession must be within ChatSessionProvider");
  return ctx;
};
